import type { CSSProperties } from "react";
import { Printer } from "lucide-react";
import {
  expsByRecency,
  getEducation,
  getProfile,
  getQualifications,
  yearRange,
} from "../lib/queries";
import { data } from "../lib/data";
import { getAppConfig } from "../lib/appConfig";
import { paragraphs } from "../components/DetailBits";

// Print/PDF is intentionally light regardless of the day/night toggle — a dark
// résumé PDF reads badly and wastes printer ink. Theme tokens live on <html>
// via [data-theme], so the light palette is re-pinned here for this page only.
export const LIGHT_TOKENS = {
  colorScheme: "light",
  // Re-evaluate inherited text color inside this scope — without it the sheet
  // inherits body's already-computed dark-theme ink.
  color: "var(--ink)",
  "--paper": "#fafaf9",
  "--paper-2": "#f2f2f0",
  "--tint-a": "color-mix(in oklab, var(--accent) 10%, transparent)",
  "--tint-b": "color-mix(in oklab, var(--warm) 8%, transparent)",
  "--card": "#ffffff",
  "--card-2": "#f7f7f5",
  "--ink": "#1b1a17",
  "--ink-soft": "#2c2a25",
  "--muted-strong": "#4a463d",
  "--muted": "#847b6d",
  "--faint": "#b3a99a",
  "--rule": "#e7e5e0",
  "--rule-strong": "#efedea",
  "--shadow": "24 22 18",
  "--accent-soft": "color-mix(in oklab, var(--accent) 14%, var(--card))",
  "--accent-line": "color-mix(in oklab, var(--accent) 40%, var(--rule))",
  "--accent-text": "color-mix(in oklab, var(--accent) 78%, var(--ink))",
} as CSSProperties;

function linkedinDisplay(url: string): string {
  return url.replace(/^https?:\/\/(www\.)?/, "").replace(/\/$/, "");
}

export function Print() {
  const p = getProfile();
  // Contact email comes from the Admin Profile (config) so it stays uniform with the rest of the site.
  const contactEmail = getAppConfig().profile.contactEmail?.trim() || p.email;
  const exps = expsByRecency();
  const education = getEducation();
  const certifications = getQualifications().filter(
    (q) => q.id !== "mba-unc" && q.id !== "bs-etce",
  );

  return (
    <div className="print-page wrap-narrow section" style={LIGHT_TOKENS}>
      <div className="print-toolbar">
        <p className="section-sub" style={{ margin: 0 }}>
          A clean, one-document résumé. Use your browser's print dialog to save as PDF.
        </p>
        <button className="btn btn-primary btn-sm" onClick={() => window.print()}>
          <Printer size={15} /> Print / Save PDF
        </button>
      </div>

      <div className="sheet" id="resume-sheet">
        <header className="sheet-head">
          <div>
            <h1 className="display sheet-name">{p.name}</h1>
            <div className="sheet-role">{p.headline}</div>
          </div>
          <div className="sheet-contact mono">
            {p.location && <div>{p.location}</div>}
            {contactEmail && <div>{contactEmail}</div>}
            {p.linkedin && <div>{linkedinDisplay(p.linkedin)}</div>}
          </div>
        </header>

        <section className="sheet-block">
          <h2 className="sheet-h2">Summary</h2>
          <div className="sheet-summary">{paragraphs(p.summary)}</div>
        </section>

        <section className="sheet-block">
          <h2 className="sheet-h2">Experience</h2>
          {exps.map((e) => (
            <div className="sheet-exp" key={e.id}>
              <div className="sheet-exp-top">
                <span className="sheet-exp-co">{e.company}</span>
                <span className="sheet-exp-dates mono">{yearRange(e.start, e.end)}</span>
              </div>
              <div className="sheet-exp-role">{e.role}</div>
              <p className="sheet-exp-sum">{e.summary}</p>
            </div>
          ))}
        </section>

        <div className="sheet-cols">
          <section className="sheet-block">
            <h2 className="sheet-h2">Education</h2>
            {education.map((e) => (
              <div className="sheet-mini" key={e.id}>
                <div className="sheet-mini-a">{e.degree}</div>
                <div className="sheet-mini-b">
                  {e.institution}
                  {e.start && e.end ? ` · ${e.start}–${e.end}` : ""}
                </div>
              </div>
            ))}
            <h2 className="sheet-h2" style={{ marginTop: 16 }}>
              Certifications
            </h2>
            {certifications.map((q) => (
              <div className="sheet-mini" key={q.id}>
                <div className="sheet-mini-a">{q.label}</div>
                <div className="sheet-mini-b">
                  {q.issuer}
                  {q.year ? ` · ${q.year}` : ""}
                </div>
              </div>
            ))}
          </section>
          <section className="sheet-block">
            <h2 className="sheet-h2">Core skills</h2>
            <ul className="sheet-skills">
              {data.skills.map((s) => (
                <li key={s.id}>{s.label}</li>
              ))}
            </ul>
            <h2 className="sheet-h2" style={{ marginTop: 16 }}>
              Industries
            </h2>
            <div className="sheet-inline">{data.industries.map((i) => i.label).join(" · ")}</div>
          </section>
        </div>
      </div>
    </div>
  );
}
